import { useEffect, useContext, useState, useRef, useCallback } from "react";
import { v4 as uuidv4 } from "uuid";

import { HttpStatusCodes } from "types/mqtt-api";
import { MqttClientContext } from "services/mqtt-client";
import { RequestTopics, Topics } from "services/topics";

interface QueryOptions {
  requestTopic: RequestTopics;
  responseTopic?: string | Topics;
  request?: any;
  onSuccess?: (data: any, request?: any) => void;
  onError?: (error: Error | any) => void;
  enabled?: boolean;
  timeout?: number | boolean;
  delayLoading?: boolean;
}

const useMqttApi = <T = any>(options: QueryOptions) => {
  const client = useContext(MqttClientContext);

  const {
    responseTopic = `frontend/response/${uuidv4().replace(/-/gi, "")}`,
    requestTopic,
    request = { method: "POST" },
    enabled = true,
    timeout = 10000,
    onSuccess,
    onError,
  } = options;

  const [data, setData] = useState<T | null>(null);
  const [error, setError] = useState<Error | null>(null);
  const [loading, setLoading] = useState(enabled);

  const timeoutIdRef = useRef<NodeJS.Timeout>();
  const cancelTokenRef = useRef<number>(0);
  const messageHandler = useRef<(topic: string, message: Buffer) => void>();

  const handleSuccess = (response: any) => {
    setData(response);
    setError(null);

    if (onSuccess) {
      onSuccess(response, request);
    }

    cleanUp();
  };

  const fetch = useCallback(
    (requestBody = request) => {
      if (!data) {
        setLoading(true);
        setData(null);
      }
      // Increment the cancellation token to cancel previous fetches
      cancelTokenRef.current += 1;
      const currentCancelToken = cancelTokenRef.current;

      if (!client || !client.connected) {
        const customError = new Error("Error: No server connection");
        setError(customError);
        setLoading(false);
        if (onError) onError(customError);
        clearTimeout(timeoutIdRef.current);
        return;
      }

      client.subscribe(responseTopic, (err) => {
        if (!err) {
          delete requestBody.response_topic; // request body should not contain respone topic

          const fullRequestBody = {
            response_topic: responseTopic,
            ...requestBody,
          };

          client.publish(requestTopic, JSON.stringify(fullRequestBody));
        }
      });

      // Remove previous message handler
      if (messageHandler.current) {
        client?.removeListener("message", messageHandler.current);
      }

      messageHandler.current = (topic, message) => {
        handleResponse(topic, message, currentCancelToken);
      };

      client.on("message", messageHandler.current);

      // Cancel previous timeout if exists
      clearTimeout(timeoutIdRef.current);

      if (timeout && typeof timeout === "number") {
        timeoutIdRef.current = setTimeout(handleTimeout, timeout);
      }
    },
    [client?.connected]
  );

  const handleTimeout = () => {
    const customError = new Error("Timeout: No response received");

    setError(customError);

    if (onError) onError(customError);
    cleanUp();
  };

  const handleError = (response: any) => {
    setError(response);
    if (onError) onError(response);

    cleanUp();
  };

  const handleResponse = (
    topic: string,
    message: Buffer,
    currentCancelToken
  ) => {
    if (
      topic !== responseTopic ||
      currentCancelToken !== cancelTokenRef.current
    ) {
      // This request is canceled, so do nothing with the response
      return;
    }

    try {
      const response = JSON.parse(message.toString());

      if (response.code === HttpStatusCodes.INFO) {
        setData(response);
        setError(null);
        setLoading(false);
        return;
      }

      if (
        [
          HttpStatusCodes.BAD_REQUEST,
          HttpStatusCodes.INTERNAL_SERVER_ERROR,
          HttpStatusCodes.NOT_FOUND,
          HttpStatusCodes.CONFLICT,
        ].includes(response.code)
      ) {
        handleError(response);
        return;
      }

      if (!response.code || response.code === HttpStatusCodes.OK) {
        handleSuccess(response);
      }
    } catch (error) {
      handleError(new Error("Error: Invalid response"));
    }
  };

  const cleanUp = () => {
    setLoading(false);
    clearTimeout(timeoutIdRef.current);
    if (messageHandler.current) {
      client?.removeListener("message", messageHandler.current);
    }
    client?.unsubscribe(responseTopic);
  };

  useEffect(() => {
    if (enabled) {
      fetch();
    }

    return () => {
      cleanUp();
    };
  }, [enabled, fetch]);

  return { data, loading, error, fetch };
};

export default useMqttApi;
